import React from 'react';
import { Form, Spinner } from 'react-bootstrap';
import Button from '../../UI/button';
import ThInputSelect from '../../forms/th-input-select';
import ThInputText from '../../forms/th-input-text';
import { useUserSettings } from '../../../shared/hooks/useUserSettings';
import { INotificationPreferences } from '../../../types/profile.types';

const DIGEST_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'never', label: 'Never' }
];

interface ToggleProps {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

const Toggle: React.FC<ToggleProps> = ({ label, checked, onChange }) => (
  <Form.Check
    className='mb-2'
    type='checkbox'
    label={label}
    checked={checked}
    onChange={(e) => onChange(e.target.checked)}
  />
);

type Props = {};

const NotificationSettingsPage = (props: Props) => {
  const { settings, loading, updateNotifications } = useUserSettings();
  const [notifs, setNotifs] = React.useState<INotificationPreferences | null>(
    null
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);

  React.useEffect(() => {
    if (settings && !notifs) {
      setNotifs({ ...settings.notifications });
    }
  }, [settings]);

  const setField = <K extends keyof INotificationPreferences>(
    key: K,
    value: INotificationPreferences[K]
  ) => {
    setNotifs((prev) => (prev ? { ...prev, [key]: value } : null));
  };

  const handleSave = async () => {
    if (!notifs) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateNotifications(notifs);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner animation='border' />;
  if (!notifs) return null;

  return (
    <div className='notification-settings'>
      <h6>Channels</h6>
      <div className='notification-settings-checkboxes notification-settings-checkboxes--spaced'>
        <Toggle
          label='Email Notifications'
          checked={notifs.emailEnabled}
          onChange={(v) => setField('emailEnabled', v)}
        />
        <Toggle
          label='Push Notifications'
          checked={notifs.pushEnabled}
          onChange={(v) => setField('pushEnabled', v)}
        />
        <Toggle
          label='In-App Notifications'
          checked={notifs.inAppEnabled}
          onChange={(v) => setField('inAppEnabled', v)}
        />
      </div>

      <h6>Categories</h6>
      <div className='notification-settings-checkboxes notification-settings-checkboxes--spaced'>
        <Toggle
          label='New follows'
          checked={notifs.followNotifications}
          onChange={(v) => setField('followNotifications', v)}
        />
        <Toggle
          label='New likes'
          checked={notifs.likeNotifications}
          onChange={(v) => setField('likeNotifications', v)}
        />
        <Toggle
          label='Comments'
          checked={notifs.commentNotifications}
          onChange={(v) => setField('commentNotifications', v)}
        />
        <Toggle
          label='Messages'
          checked={notifs.messageNotifications}
          onChange={(v) => setField('messageNotifications', v)}
        />
        <Toggle
          label='System alerts'
          checked={notifs.systemNotifications}
          onChange={(v) => setField('systemNotifications', v)}
        />
        <Toggle
          label='Trip updates'
          checked={notifs.tripUpdateNotifications}
          onChange={(v) => setField('tripUpdateNotifications', v)}
        />
        <Toggle
          label='Recommendations'
          checked={notifs.recommendationNotifications}
          onChange={(v) => setField('recommendationNotifications', v)}
        />
      </div>

      <h6>Digest</h6>
      <div className='notification-settings-checkboxes'>
        <Toggle
          label='Enable email digest'
          checked={notifs.digestEnabled}
          onChange={(v) => setField('digestEnabled', v)}
        />
        <ThInputSelect
          label='Digest Frequency'
          value={notifs.digestFrequency}
          options={DIGEST_OPTIONS}
          onChange={(v: string) =>
            setField(
              'digestFrequency',
              v as INotificationPreferences['digestFrequency']
            )
          }
          mb={16}
        />
      </div>

      <h6>Quiet Hours</h6>
      <div className='notification-settings-checkboxes'>
        <Toggle
          label='Enable quiet hours'
          checked={notifs.quietHoursEnabled}
          onChange={(v) => setField('quietHoursEnabled', v)}
        />
        {notifs.quietHoursEnabled && (
          <div className='d-flex gap-3'>
            <ThInputText
              label='Start'
              value={notifs.quietHoursStart ?? ''}
              onChange={(v: string) => setField('quietHoursStart', v)}
              placeholder='22:00'
              mb={16}
            />
            <ThInputText
              label='End'
              value={notifs.quietHoursEnd ?? ''}
              onChange={(v: string) => setField('quietHoursEnd', v)}
              placeholder='07:00'
              mb={16}
            />
          </div>
        )}
      </div>

      {error && <p className='text-danger small'>{error}</p>}
      {success && <p className='text-success small'>Settings saved.</p>}

      <div className='notification-settings-actions'>
        <Button
          variant='primary'
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <Spinner
              size='sm'
              animation='border'
            />
          ) : (
            'Update'
          )}
        </Button>
      </div>
    </div>
  );
};
export default NotificationSettingsPage;
